import { useState, useEffect, useRef, type CSSProperties, type ChangeEvent } from 'react';

interface NumericInputProps {
    value: number;
    onChange: (value: number) => void;
    step?: number;
    style?: CSSProperties;
    placeholder?: string;
    disabled?: boolean;
}

/**
 * Controlled numeric input that allows typing negative numbers.
 * Uses text input internally so '-' can be entered before the digits,
 * then commits the parsed number to the parent on every valid keystroke
 * and normalises the display on blur.
 */
export default function NumericInput({ value, onChange, step: _step, style, placeholder, disabled }: NumericInputProps) {
    const [raw, setRaw] = useState(String(value));
    const committedRef = useRef(value);

    // Sync display when the parent pushes a new value (e.g. form reset),
    // but only if it differs from the last value we committed ourselves.
    useEffect(() => {
        if (value !== committedRef.current) {
            setRaw(String(value));
            committedRef.current = value;
        }
    }, [value]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const s = e.target.value;
        setRaw(s);
        const parsed = parseFloat(s);
        if (!isNaN(parsed)) {
            committedRef.current = parsed;
            onChange(parsed);
        }
    };

    const handleBlur = () => {
        const parsed = parseFloat(raw);
        if (isNaN(parsed)) {
            // Reset display to the last committed value
            setRaw(String(committedRef.current));
        } else {
            setRaw(String(parsed));
        }
    };

    return (
        <input
            type="text"
            inputMode="decimal"
            value={raw}
            onChange={handleChange}
            onBlur={handleBlur}
            style={style}
            placeholder={placeholder}
            disabled={disabled}
        />
    );
}
